import clsx from 'clsx';
import * as React from 'react';

import type { BlockParagraphData } from '@gitbook/doc-core';
import {
    BLOCK_COLUMN,
    BLOCK_COLUMNS,
    BLOCK_LIST_ITEM,
    BLOCK_STEPPER_STEP,
    BLOCK_TABS_ITEM,
    FRAGMENT_ANNOTATION_BODY,
    FRAGMENT_EXPANDABLE_BODY,
    FRAGMENT_EXPANDABLE_TITLE,
    isBlockEmpty,
    isDocumentEmpty,
} from '@gitbook/doc-core';
import type { Block, Document, Fragment } from '@gitbook/slate';
import type { BlockProps, EditorContainer } from '@gitbook/slate-react';
import { assert } from '@gitbook/util-ts';

import { BlockWrapper } from '../common';
import { useTextBlockPaletteFactory } from '../common/textBlockPalette';
import { type BlockVariant, BlockVariantContext } from '../contexts';
import { TextAlignment, TextAlignmentContext, textAlignmentClassName } from '../text-alignment';
import { InsertBlockWrapper } from './InsertBlockWrapper';

const blockVariantClassName: Record<BlockVariant, string> = {
    caption: 'text-sm text-ui-small text-muted',
    expandableTitle: 'text-ui-heading-small leading-6',
    quote: 'text-content-paragraph text-muted',
    root: 'text-content-paragraph',
    hint: 'text-content-paragraph-small',
};

/*
 * Renderer for a paragraph block.
 */
export function BlockParagraph(props: BlockProps): React.ReactElement {
    const { editor, node, parent, children, attributes } = props;

    const blockVariant = React.useContext(BlockVariantContext);
    const parentTextAlignment = React.useContext(TextAlignmentContext);

    const data = node.data.toFrozenObject() as BlockParagraphData;
    const textAlignment = data?.align ? data.align : parentTextAlignment;

    const blockClassName = textAlignmentClassName[textAlignment];

    const textClassName = clsx(
        'select-text whitespace-pre-wrap',
        blockVariantClassName[blockVariant]
    );

    const content = (
        <TextAlignmentContext.Provider value={textAlignment}>
            <span className={textClassName}>{children}</span>
        </TextAlignmentContext.Provider>
    );

    const palette = useTextBlockPaletteFactory(node.key);
    const isEmpty = isBlockEmpty(node);
    const placeholder = isEmpty ? getPlaceholder(node, parent, editor) : null;

    // Basic paragraph when rendering in fragments
    if (!shouldWrapWithControls(parent)) {
        return (
            <div
                className={clsx('relative', blockClassName, {
                    'block-highlight': !editor.readOnly && props.isBlockSelection,
                })}
                data-selected={!editor.readOnly && props.isSelectedBlock ? '' : undefined}
                {...attributes}
            >
                {!editor.readOnly && placeholder && isEmpty ? (
                    <div contentEditable={false} className="pointer-events-none absolute inset-0">
                        <span className={clsx(textClassName, 'text-muted')}>{placeholder}</span>
                    </div>
                ) : null}
                {content}
            </div>
        );
    }

    if (!editor.readOnly && isEmpty && textAlignment === TextAlignment.Start) {
        return (
            <InsertBlockWrapper
                {...props}
                palette={palette}
                placeholder={placeholder}
                contentProps={{
                    className: blockClassName,
                }}
            >
                {content}
            </InsertBlockWrapper>
        );
    }

    return (
        <BlockWrapper
            {...props}
            palette={palette}
            contentProps={{
                className: blockClassName,
            }}
        >
            {content}
        </BlockWrapper>
    );
}

/**
 * Return true if the paragraph should be wrapped in a BlockWrapper with control buttons.
 */
function shouldWrapWithControls(parent: Block | Document | Fragment): boolean {
    if (
        // Content in an expandable body
        parent.type === FRAGMENT_EXPANDABLE_BODY ||
        parent.type === FRAGMENT_EXPANDABLE_TITLE
    ) {
        return true;
    }

    if (parent.object === 'fragment') {
        return false;
    }

    if (parent.type === BLOCK_LIST_ITEM) {
        // The parent component rendering the list item is responsible for the controls
        return false;
    }

    return true;
}

/**
 * Return a text placeholder for a paragraph block depending on its parent.
 */
function getPlaceholder(
    _node: Block,
    parent: Block | Document | Fragment,
    editor: EditorContainer
): string | null {
    const isEmpty = parent.object === 'block' ? isBlockEmpty(parent) : isDocumentEmpty(parent);

    if (isEmpty && parent.type === FRAGMENT_EXPANDABLE_TITLE) {
        return 'Title';
    }

    if (isEmpty && parent.type === FRAGMENT_EXPANDABLE_BODY) {
        return 'Expandable content';
    }

    if (isEmpty && parent.type === FRAGMENT_ANNOTATION_BODY) {
        return 'Annotate this text';
    }

    if (isEmpty && parent.type === BLOCK_TABS_ITEM) {
        return 'Tab content';
    }

    if (parent.type === BLOCK_STEPPER_STEP) {
        // For stepper, we show the placeholder even if the heading has content
        // We show it when this block is the only empty paragraph
        if (parent.nodes.size > 2) {
            return null;
        }
        return 'Step content';
    }

    if (parent.type === BLOCK_COLUMN && isEmpty) {
        const columnsBlock = editor.value.document.getParent(parent.key);
        assert(
            columnsBlock?.type === BLOCK_COLUMNS,
            'Parent of a column should be a columns block'
        );

        if (columnsBlock.nodes.get(0)?.key === parent.key) {
            return 'First column';
        }

        if (columnsBlock.nodes.get(1)?.key === parent.key) {
            return 'Second column';
        }

        if (columnsBlock.nodes.get(2)?.key === parent.key) {
            return 'Third column';
        }

        return 'Column content';
    }

    return null;
}
